import type { ReactNode } from 'react';

import { buttons } from '@/styles/buttons';

import { classNames } from '@/utils/classnames';

// A non-text label (e.g. an icon) has no accessible name of its own, so it
// must bring an explicit aria-label.
type ChartOption<T extends string> = { value: T } & (
  | { label: string; ariaLabel?: string }
  | { label: ReactNode; ariaLabel: string }
);

type ChartOptionSelectorProps<T extends string> = {
  ariaLabel: string;
  options: readonly ChartOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
};

export const ChartOptionSelector = <T extends string>({
  ariaLabel,
  options,
  value,
  onChange,
  className,
}: ChartOptionSelectorProps<T>) => (
  <div
    className={classNames(
      'flex shrink-0 items-center rounded-xl border border-token-gray-600 p-0.5',
      className,
    )}
    role="group"
    aria-label={ariaLabel}
  >
    {options.map(({ label, value: optionValue, ariaLabel: optionAriaLabel }) => (
      <button
        key={optionValue}
        type="button"
        aria-label={optionAriaLabel}
        aria-pressed={optionValue === value}
        data-selected={optionValue === value}
        className={buttons({
          size: 'extraSmall',
          shape: 'pill',
          color: 'transparent',
          class: [
            'min-w-8 px-2 flex-grow text-token-gray-800 data-[selected=false]:hover:bg-transparent!',
            'data-[selected=true]:bg-token-alphalight-200',
            'hover:text-token-gray-1600 data-[selected=true]:text-token-gray-1600',
          ],
        })}
        onClick={() => onChange(optionValue)}
      >
        {label}
      </button>
    ))}
  </div>
);
